import {
  EnvironmentVariableUtilities,
  USER_ROLES,
  NavigationNodeId,
} from '@pwc/pwc-deals-web-common';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useLocation, useRouteMatch } from 'react-router-dom';

import { useSelector } from 'react-redux';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import featureFlags from 'common/helpers/featureFlags';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import {
  getUserPreferences,
  GROUP_SETTING_KEY,
} from '../../common/services/userPreferencesProjectService';
import { analysesPreferredGroupListPath } from '../analysesGroups/components/AnalysesGroupsContentRouter/AnalysesGroupsContentRouter';
import Breadcrumbs from './Breadcrumbs';
import { categoryPathRoute } from '../divestitureDiagnostic/routes';
import { useCustomRouteNames } from '../../common/hooks/routeHookHelpers';

const ANALYSES_LISTING_PAGE_URL = '/projects/:projectId/analyses';
const STRUCTURED_ANALYSES_BASE_PAGE_URL =
  '/projects/:projectId/template-analyses';

const getDisplayName = (name) =>
  name.length > 25 ? `${name.substring(0, 25)}...` : name;

const getProjectPath = (isPwcUser) => {
  if (!featureFlags.isPOTopicRefactorEnabled) {
    return '/projects/:projectId';
  }

  if (isPwcUser) {
    return '/projects/:projectId/topics/draft';
  }

  return '/projects/:projectId/topics/shared';
};

const replaceSlugs = (params, path) => {
  const paramsKeys = Object.keys(params);
  const hasParams = paramsKeys.length > 0;

  if (!hasParams) {
    return path;
  }

  return paramsKeys.reduce(
    (desluggedPath, param) => desluggedPath.replace(`:${param}`, params[param]),
    path
  );
};

/**
 * Removes "/files" route for breadcrumbs if isEnabledFileFoldersFeature flag is enabled.
 *
 * For new folders feature (enabled by instance flag) we need both routes /files/folders & /files/list
 * to behave like "last route", so we need to remove "/files" to make the "/files/folders" the last one
 * @param {object} breadcrumbRoute - Contains route and path
 * @returns boolean - False if we need to remove that breadcrumbRoute, false otherwise
 */
const filterFileBreadcrumbForFolders = (breadcrumbRoute) =>
  featureFlags.isEnabledFileFoldersFeature
    ? breadcrumbRoute.path !== '/projects/:projectId/files'
    : true;

const filterTeamMemberBreadCrumb = (breadcrumbs) => {
  let indexToBeRemoved = 0;
  let count = 0;
  breadcrumbs.forEach((breadCrumb, index) => {
    if (breadCrumb.text === 'Team members') {
      if (count === 0) {
        indexToBeRemoved = index;
      }
      count += 1;
    }
  });
  if (count > 1) {
    breadcrumbs.splice(indexToBeRemoved, 1);
  }
};

async function loadAnalysisGroupByPreferences(
  projectId,
  setDefaultAnalysisGroupBy
) {
  try {
    const groupByPreferences = await getUserPreferences(
      projectId,
      GROUP_SETTING_KEY
    );
    const groupBySettingValue = groupByPreferences?.[0]?.settingValue;

    if (!groupBySettingValue) return;

    setDefaultAnalysisGroupBy(groupBySettingValue);
  } catch (error) {
    // ignore
  }
}

const BreadcrumbsContainer = ({
  projectId /* projectId could be null, while rendering outside project scope */,
}) => {
  const [defaultAnalysisGroupBy, setDefaultAnalysisGroupBy] =
    useState('custom');
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const location = useLocation();
  const [hide, setHide] = useState(true);
  const customNames = useCustomRouteNames();
  const { t, ready } = useTranslation();
  const analysisCustomRouteMatch = useRouteMatch(ANALYSES_LISTING_PAGE_URL);
  const isUnderCustomAnalysisRoute =
    analysisCustomRouteMatch && !analysisCustomRouteMatch.isExact;

  const structuredAnalysisRouteMatch = useRouteMatch(
    STRUCTURED_ANALYSES_BASE_PAGE_URL
  );
  const isUnderStructuredAnalysisRoute =
    structuredAnalysisRouteMatch && !structuredAnalysisRouteMatch.isExact;

  useEffect(() => {
    if (
      projectId &&
      (isUnderCustomAnalysisRoute || isUnderStructuredAnalysisRoute)
    ) {
      loadAnalysisGroupByPreferences(projectId, setDefaultAnalysisGroupBy);
    }
  }, [projectId, isUnderCustomAnalysisRoute, isUnderStructuredAnalysisRoute]);

  let kfaNavigationTitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);
  if (kfaNavigationTitle === 'Key Focus Area') {
    kfaNavigationTitle = 'breadcrumbs.keyFocusArea';
  }

  const ffAnalysesRouters = useMemo(
    () => [
      {
        path: ANALYSES_LISTING_PAGE_URL,
        pathDynamic: analysesPreferredGroupListPath(
          ':projectId',
          defaultAnalysisGroupBy
        ),
        text: 'breadcrumbs.analyses',
      },
      {
        path: '/projects/:projectId/analyses/:analysisId/view',
        customName: 'analysisTitle',
      },
      {
        path: '/projects/:projectId/analyses/:entityId/report-preview/:entityType(ANALYSIS)/:version/',
        customName: 'analysisTitle',
      },
      {
        path: '/projects/:projectId/analyses/add-type',
        text: 'breadcrumbs.addAnalyses',
      },
      {
        path: '/projects/:projectId/analyses/add-from-template',
        text: 'breadcrumbs.addFromTemplate',
      },
      { path: '/projects/:projectId/analyses/create', text: 'breadcrumbs.new' },
      {
        path: '/projects/:projectId/analyses/create/library-items',
        text: 'breadcrumbs.addData',
      },
      {
        path: '/projects/:projectId/analyses/create/layout',
        text: 'breadcrumbs.addData',
      },
      {
        path: '/projects/:projectId/analyses/create/layout/preview',
        text: 'breadcrumbs.saveAndPreviewReport',
      },
      {
        path: '/projects/:projectId/analyses/quality-of-earnings',
        text: 'Quality of Earnings',
      },
      {
        path: '/projects/:projectId/analyses/net-working-capital',
        text: 'Net Working Capital',
      },
      {
        path: '/projects/:projectId/analyses/debt-debt-like',
        text: 'Debt & Debt Like',
      },
      {
        path: '/projects/:projectId/analyses/:analysisId/edit',
        text: 'breadcrumbs.edit',
      },
      {
        path: '/projects/:projectId/analyses/:analysisId/edit/library-items',
        text: 'breadcrumbs.addData',
      },
      {
        path: '/projects/:projectId/analyses/:analysisId/edit/layout',
        text: 'breadcrumbs.addData',
      },
      {
        path: '/projects/:projectId/analyses/:analysisId/edit/layout/preview',
        text: 'breadcrumbs.saveAndPreviewReport',
      },
      // Structured Analysis 2.0
      {
        path: STRUCTURED_ANALYSES_BASE_PAGE_URL,
        pathDynamic: analysesPreferredGroupListPath(
          ':projectId',
          defaultAnalysisGroupBy
        ),
        text: 'breadcrumbs.analyses',
      },
      {
        path: '/projects/:projectId/template-analyses/:contentThreadId/view/:version',
        customName: 'structuredAnalysisTitle',
      },
      {
        path: '/projects/:projectId/template-analyses/:contentThreadId/edit',
        text: 'breadcrumbs.edit',
      },
      {
        path: '/projects/:projectId/template-analyses/:type/report-preview/:version',
        customName: 'Report Preview',
      },
    ],
    [defaultAnalysisGroupBy]
  );

  const remainingRoutes = [
    { path: '/projects', text: 'breadcrumbs.projects' },
    { path: '/projects/:projectId', customName: 'projectName' },
    { path: '/projects/:projectId/topics/draft', text: 'breadcrumbs.topics' },
    { path: '/projects/:projectId/topics/shared', text: 'breadcrumbs.topics' },
    {
      path: '/projects/:projectId/topics/:topicId/report-preview/:entityType/:version',
      customName: 'Report Preview',
    },
    {
      path: '/projects/:projectId/topic/:topicId/',
      text: 'breadcrumbs.topic',
    },
    {
      path: '/projects/:projectId/topic/:topicId/view/draft/',
      customName: 'topicTitle',
    },
    {
      path: '/projects/:projectId/topic/:topicId/view/shared/',
      customName: 'topicTitle',
    },
    {
      path: '/projects/:projectId/topic/:topicId/edit/draft',
      text: 'breadcrumbs.edit',
    },
    {
      path: '/projects/:projectId/topic/:topicId/view/report-preview/:entityType/:version',
      text: 'breadcrumbs.reportPreview',
    },
    {
      path: '/projects/:projectId/topic/:topicId/edit/report-preview/:entityType/:version',
      text: 'breadcrumbs.reportPreview',
    },
    { path: '/projects/:projectId/insights', text: 'breadcrumbs.insights' },
    {
      path: '/projects/:projectId/content-hub',
      text: 'breadcrumbs.contentHub',
    },
    {
      path: '/projects/:projectId/content-set',
      text: 'breadcrumbs.contentSet',
    },
    { path: '/projects/:projectId/insights/create', text: 'breadcrumbs.new' },
    {
      path: '/projects/:projectId/insights/:insightId/view',
      customName: 'insightTitle',
    },
    {
      path: '/projects/:projectId/insights/:insightId/edit',
      text: 'breadcrumbs.edit',
    },
    {
      path: '/projects/:projectId/key-focus-areas',
      text: kfaNavigationTitle,
    },
    {
      path: '/projects/:projectId/key-focus-areas/create',
      text: 'breadcrumbs.new',
    },
    {
      path: '/projects/:projectId/key-focus-areas/:hypothesisId/edit',
      text: 'breadcrumbs.edit',
    },
    {
      path: '/projects/:projectId/key-focus-areas/:hypothesisId/view',
      customName: 'hypothesisTitle',
    },
    {
      path: '/projects/:projectId/key-focus-areas/:hypothesisId/report-preview/:entityType(KFA)/:version',
      customName: 'Report Preview',
    },
    {
      path: '/projects/:projectId/reports',
      text: 'breadcrumbs.reports',
    },
    {
      path: '/projects/:projectId/reports/create-report',
      text: 'breadcrumbs.reportBuilder',
    },
    {
      path: '/projects/:projectId/reports/create-report/filter',
      text: 'breadcrumbs.filteredResults',
    },
    {
      path: '/projects/:projectId/reports/create-report/filter/preview/cover',
      text: 'breadcrumbs.cover',
    },
    {
      path: '/projects/:projectId/reports/create-report/filter/preview/tile/:tileId',
      customName: 'tileTitle',
    },
    {
      path: '/projects/:projectId/reports/create-report/preview/report',
      text: 'breadcrumbs.reportPreview',
    },
    {
      path: '/projects/:projectId/reports/create-report/preview/cover',
      text: 'breadcrumbs.cover',
    },
    {
      path: '/projects/:projectId/reports/create-report/preview/tile/:tileId',
      customName: 'tileTitle',
    },

    { path: '/projects/:projectId/settings', text: 'breadcrumbs.settings' },
    {
      path: '/projects/:projectId/settings/status',
      text: 'breadcrumbs.status',
    },
    {
      path: '/projects/:projectId/settings/preferences',
      text: 'breadcrumbs.preferences',
    },
    {
      path: '/projects/:projectId/settings/disclaimer',
      text: 'breadcrumbs.disclaimer',
    },
    {
      path: '/projects/:projectId/settings/pwcteam',
      text: 'breadcrumbs.pwcTeam',
    },
    {
      path: '/projects/:projectId/settings/client',
      text: 'breadcrumbs.client',
    },
    { path: '/projects/:projectId/settings/team', text: 'breadcrumbs.team' },
    {
      path: '/projects/:projectId/settings/third-party',
      text: 'breadcrumbs.thirdParty',
    },
    {
      path: '/projects/:projectId/settings/notifications',
      text: 'breadcrumbs.notifications',
    },
    {
      path: '/projects/:projectId/project-disclaimer',
      text: 'breadcrumbs.projectDisclaimer',
    },
    {
      path: '/projects/:projectId/divestiture-diagnostic/survey/:sectionPath/:questionId',
      text: 'breadcrumbs.diagnosticQuestionnaire',
    },
    {
      path: '/projects/:projectId/divestiture-diagnostic/survey/responses',
      text: 'breadcrumbs.diagnosticQuestionnaire',
    },
    {
      path: categoryPathRoute,
      text: 'breadcrumbs.diagnosticQuestionnaire',
    },
    {
      path: '/projects/:projectId/notifications/',
      text: 'breadcrumbs.notifications',
    },
    {
      path: '/projects/:projectId/notifications/mentions',
      text: 'breadcrumbs.mentions',
    },
    {
      path: '/projects/:projectId/notifications/approval-requests',
      text: 'breadcrumbs.approvalRequest',
    },
    {
      path: '/projects/:projectId/notifications/project-activity',
      text: 'breadcrumbs.projectActivity',
    },
    {
      path: '/projects/:projectId/files',
      text: 'breadcrumbs.files',
    },
    {
      path: '/projects/:projectId/files/folders',
      text: 'breadcrumbs.filesFolders',
    },
    {
      path: '/projects/:projectId/files/list',
      text: 'breadcrumbs.filesList',
    },
    {
      path: '/projects/:projectId/analyses-groups',
      text: 'breadcrumbs.groups',
    },
    {
      path: '/projects/:projectId/analyses-groups/by-kfa',
      text: 'breadcrumbs.keyFocusArea',
    },
    {
      path: '/projects/:projectId/analyses-groups/custom',
      text: 'breadcrumbs.custom',
    },
    {
      path: '/projects/:projectId/e729cc9b-fcee-4d70-9eae-9884dc421714',
      text: 'breadcrumbs.sharepointPerformanceTest',
    },
    {
      path: '/projects/:projectId/team-members',
      text: 'breadcrumbs.teamMembers',
    },
    {
      path: '/projects/:projectId/team-members/engagement-team/members',
      text: 'breadcrumbs.teamMembers',
    },
    {
      path: '/projects/:projectId/team-members/engagement-team/requests',
      text: 'breadcrumbs.teamMembers',
    },
    {
      path: '/projects/:projectId/team-members/client-team/members',
      text: 'breadcrumbs.teamMembers',
    },
    {
      path: '/projects/:projectId/team-members/client-team/requests',
      text: 'breadcrumbs.teamMembers',
    },
    {
      path: '/projects/:projectId/team-members/third-parties/members',
      text: 'breadcrumbs.teamMembers',
    },
    {
      path: '/projects/:projectId/team-members/third-parties/requests',
      text: 'breadcrumbs.teamMembers',
    },
  ];

  const allRoutes = remainingRoutes.concat(ffAnalysesRouters);
  const path = location.pathname;

  useEffect(() => {
    setHide(
      path.toLowerCase() === '/unauthorized' ||
        path.toLowerCase() === '/notfound' ||
        path.toLowerCase() === '/unauthenticated'
    );
  }, [path]);

  if (hide) {
    return null;
  }

  if (path.indexOf('policies') > -1) {
    const breadcrumbs = [
      { text: t('breadcrumbs.projects'), path: '/projects' },
      { text: t('breadcrumbs.termsAndConditions'), path },
    ];
    return (
      <div>
        <span className="breadcrumb-root">Junction</span>
        {' | '}
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </div>
    );
  }

  if (!ready) {
    return null;
  }
  return (
    <div>
      <span className="breadcrumb-root">
        {EnvironmentVariableUtilities.platformName}
      </span>
      {' | '}
      {allRoutes.map((route) => {
        return (
          <Route
            exact
            path={route.path}
            key={route.path}
            render={(props) => {
              const routeParams = props.match.params;
              const routePath = props.match.path;

              let breadcrumbs;

              if (routeParams.projectId === 'create-project') {
                breadcrumbs = [
                  { text: t('breadcrumbs.projects'), path: '/projects' },
                  {
                    text: t('breadcrumbs.new'),
                    path: '/projects/create-project',
                  },
                ];
              } else {
                breadcrumbs = remainingRoutes
                  .concat(ffAnalysesRouters)
                  .filter((breadcrumbRoute) =>
                    routePath.includes(breadcrumbRoute.path)
                  )
                  .filter((breadcrumbRoute) =>
                    filterFileBreadcrumbForFolders(breadcrumbRoute)
                  )
                  .map((breadcrumbRoute) => {
                    const isProjectName =
                      breadcrumbRoute.customName === 'projectName';

                    // eslint-disable-next-line no-nested-ternary
                    const breadcrumbPath = isProjectName
                      ? getProjectPath(isPwcUser)
                      : breadcrumbRoute.pathDynamic
                      ? breadcrumbRoute.pathDynamic
                      : breadcrumbRoute.path;

                    return {
                      text: breadcrumbRoute.customName
                        ? getDisplayName(
                            customNames[breadcrumbRoute.customName] || ''
                          )
                        : t(breadcrumbRoute.text),
                      path: replaceSlugs(routeParams, breadcrumbPath),
                    };
                  });
                filterTeamMemberBreadCrumb(breadcrumbs);
              }
              return <Breadcrumbs breadcrumbs={breadcrumbs} />;
            }}
          />
        );
      })}
    </div>
  );
};

export default BreadcrumbsContainer;
